import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import type { DMCoreSpaceOperatorLoginBodyData } from '#src/interfaces/DMCoreSpaceOperator'
import { dmCoreConfig } from '#src/plugins/dmCoreConfigPlugin'
import { useDmCoreSpaceOperatorAuthStore } from '#src/stores/dmCoreSpaceOperatorAuthStore'

export const useOperatorLogin = () => {
  const REMEMBER_USERNAME_LS_KEY = `${dmCoreConfig.appName}_settings_remember_username`
  const username = ref('')
  const password = ref('')
  const shouldRememberUsername = ref(false)
  const shouldHidePlainPassword = ref(true)
  const loginErrorMessage = ref('')
  const loginErrorCode = ref('')

  const { isLoginLoading, isLogoutLoading } = storeToRefs(useDmCoreSpaceOperatorAuthStore())
  const { loginOperator: performLoginOperator } = useDmCoreSpaceOperatorAuthStore()

  const togglePasswordVisibilityIcon = computed(() =>
    shouldHidePlainPassword.value ? 'eye-b' : 'eye-c'
  )
  const passwordVisibilityType = computed(() =>
    shouldHidePlainPassword.value ? 'password' : 'text'
  )
  const hasLoginErrors = computed(() => loginErrorMessage.value.length > 0)
  const hasLoginCredentialsError = computed(() => [400, 401].includes(Number(loginErrorCode.value)))
  const hasLoginFieldsEmpty = computed(
    () => username.value.trim() === '' || password.value.trim() === ''
  )
  const isLoginButtonDisabled = computed(() => isLoginLoading.value || hasLoginFieldsEmpty.value)

  onMounted(() => {
    autoPopulateRememberedUsername()
  })

  const togglePasswordVisibility = () => {
    shouldHidePlainPassword.value = !shouldHidePlainPassword.value
  }

  const loginOperator = async (loginData: DMCoreSpaceOperatorLoginBodyData) => {
    await performLoginOperator(loginData)
    storeUsernameToLocalStorage()
  }

  const storeUsernameToLocalStorage = () => {
    if (shouldRememberUsername.value) {
      localStorage.setItem(
        REMEMBER_USERNAME_LS_KEY,
        JSON.stringify({
          username: username.value
        })
      )
    }
  }

  const autoPopulateRememberedUsername = () => {
    const lsData = localStorage.getItem(REMEMBER_USERNAME_LS_KEY)
    const rememberedUsername = lsData ? JSON.parse(lsData)?.username : null

    if (rememberedUsername) {
      username.value = rememberedUsername
      shouldRememberUsername.value = true
    }
  }

  return {
    username,
    password,
    shouldRememberUsername,
    togglePasswordVisibilityIcon,
    passwordVisibilityType,
    shouldHidePlainPassword,
    hasLoginErrors,
    hasLoginCredentialsError,
    loginErrorMessage,
    loginErrorCode,
    togglePasswordVisibility,
    storeUsernameToLocalStorage,
    autoPopulateRememberedUsername,
    isLoginLoading,
    isLogoutLoading,
    isLoginButtonDisabled,
    loginOperator
  }
}
